import { config } from "../../config/env";
import { validateLandingYoutubeUrl } from "../../lib/landingSourceIntent";

const BACKEND_SOURCE_VERIFICATION_FAILURE =
  "YouTube video metadata could not be verified";
export const TOPCLIP_SOURCE_VERIFICATION_MESSAGE =
  "This video couldn’t be verified. Use one viewable, horizontal YouTube video between 5 and 90 minutes.";

export const TOPCLIP_AUTO_CAPTION_STYLES = [
  { id: "basic", label: "Basic" },
  { id: "word-focus", label: "Word Focus" },
  { id: "green-glow", label: "Green Glow" },
  { id: "comic-bold", label: "Comic Bold" },
  { id: "giant-yellow", label: "Giant Yellow" },
  { id: "giant-word", label: "Giant Word" },
  { id: "focus-fade", label: "Focus Fade" },
  { id: "karaoke-pop", label: "Karaoke Pop" },
  { id: "line-focus", label: "Line Focus" },
  { id: "lowkey", label: "Lowkey" },
  { id: "aura-bold", label: "Aura Bold" },
  { id: "glowing-bold", label: "Glowing Bold" },
  { id: "neon-rush", label: "Neon Rush" },
  { id: "angry", label: "Angry" },
  { id: "excited", label: "Excited" },
  { id: "cascade", label: "Cascade" },
  { id: "haunted-serif", label: "Haunted Serif" },
  { id: "soft-serif", label: "Soft Serif" },
] as const;

export const TOPCLIP_AUTO_LENGTH_CHOICES = [
  { id: "auto", label: "Auto" },
  { id: "short", label: "Short (15-30 sec)" },
  { id: "medium", label: "Medium (30-60 sec)" },
  { id: "long", label: "Long (60-90 sec)" },
] as const;

export type TopClipCaptionStyleId =
  (typeof TOPCLIP_AUTO_CAPTION_STYLES)[number]["id"];
export type TopClipLengthChoice =
  (typeof TOPCLIP_AUTO_LENGTH_CHOICES)[number]["id"];
export type TopClipCountMode = "auto" | "manual";

export interface TopClipAutoOptions {
  clip_count_mode: TopClipCountMode;
  clips_requested: number;
  clip_length_choice: TopClipLengthChoice;
  captions_enabled: boolean;
  caption_style_id: TopClipCaptionStyleId;
  ai_title_enabled: boolean;
}

export const DEFAULT_TOPCLIP_AUTO_OPTIONS: Readonly<TopClipAutoOptions> =
  Object.freeze({
    clip_count_mode: "auto",
    clips_requested: 6,
    clip_length_choice: "auto",
    captions_enabled: true,
    caption_style_id: "basic",
    ai_title_enabled: true,
  });

export const createDefaultTopClipAutoOptions = (): TopClipAutoOptions => ({
  ...DEFAULT_TOPCLIP_AUTO_OPTIONS,
});

export interface TopClipAutoSubmitResponse {
  job_id: string;
  status: "queued";
  message: string;
  settings: TopClipAutoOptions;
}

export interface TopClipAutoJobStatus {
  job_id: string;
  status: string;
  progress: number;
  message: string;
  current_step: string | null;
  total_clips: number;
}

export interface TopClipAutoClip {
  id?: string;
  filename: string;
  title?: string;
  duration?: number;
  file_size?: number;
  hook_title?: string;
  viral_potential?: string;
  stream_url?: string;
}

export interface TopClipAutoClipsResponse {
  job_id: string;
  total: number;
  clips: TopClipAutoClip[];
}

export type TopClipAutoApiErrorKind =
  | "authentication"
  | "network"
  | "rate_limited"
  | "request"
  | "invalid_response";

export class TopClipAutoApiError extends Error {
  readonly kind: TopClipAutoApiErrorKind;
  readonly status: number | null;
  readonly retryAfterSeconds: number | null;
  readonly error: string | null;
  readonly field: string | null;

  constructor(
    kind: TopClipAutoApiErrorKind,
    message: string,
    options: {
      status?: number;
      retryAfterSeconds?: number | null;
      error?: string | null;
      field?: string | null;
    } = {}
  ) {
    super(message);
    this.name = "TopClipAutoApiError";
    this.kind = kind;
    this.status = options.status ?? null;
    this.retryAfterSeconds = options.retryAfterSeconds ?? null;
    this.error = options.error ?? null;
    this.field = options.field ?? null;
    Object.setPrototypeOf(this, TopClipAutoApiError.prototype);
  }
}

type FetchImplementation = typeof fetch;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const requiredToken = (token: string): string => {
  if (typeof token !== "string" || token.trim() === "") {
    throw new TopClipAutoApiError(
      "authentication",
      "Your sign-in session is unavailable. Please sign in again."
    );
  }
  return token;
};

const requiredPathSegment = (value: string, label: string): string => {
  if (typeof value !== "string" || value.trim() === "") {
    throw new TopClipAutoApiError(
      "request",
      `${label} is required before contacting TopClip.`
    );
  }
  return encodeURIComponent(value);
};

const readJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    throw new TopClipAutoApiError(
      "invalid_response",
      "TopClip returned an unreadable response.",
      { status: response.status }
    );
  }
};

const messageFromPayload = (payload: unknown, fallback: string): string => {
  if (!isRecord(payload)) return fallback;
  for (const key of ["message", "detail", "error"] as const) {
    const value = payload[key];
    if (typeof value === "string" && value.trim() !== "") return value;
  }
  return fallback;
};

const responseMetadata = (
  payload: unknown
): { error: string | null; field: string | null } => ({
  error:
    isRecord(payload) &&
    typeof payload.error === "string" &&
    payload.error.trim() !== ""
      ? payload.error
      : null,
  field:
    isRecord(payload) &&
    typeof payload.field === "string" &&
    payload.field.trim() !== ""
      ? payload.field
      : null,
});

export const parseRetryAfterSeconds = (
  value: string | null,
  nowMilliseconds = Date.now()
): number | null => {
  if (value === null) return null;
  const candidate = value.trim();
  if (/^\d+$/.test(candidate)) return Number(candidate);
  const retryDate = Date.parse(candidate);
  if (!Number.isFinite(retryDate)) return null;
  return Math.max(0, Math.ceil((retryDate - nowMilliseconds) / 1000));
};

const throwForHttpError = async (response: Response): Promise<never> => {
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    // A stable generic message is safer than retaining an unreadable body.
  }
  const metadata = responseMetadata(payload);

  if (response.status === 429) {
    throw new TopClipAutoApiError(
      "rate_limited",
      messageFromPayload(
        payload,
        "A generation is already active. Wait for it to finish before trying again."
      ),
      {
        status: response.status,
        retryAfterSeconds: parseRetryAfterSeconds(
          response.headers.get("Retry-After")
        ),
        ...metadata,
      }
    );
  }

  if (response.status === 401) {
    throw new TopClipAutoApiError(
      "authentication",
      "Your sign-in session expired. Please sign in again.",
      { status: response.status, ...metadata }
    );
  }

  const backendMessage = messageFromPayload(
    payload,
    "TopClip could not complete that request."
  );
  // Older API releases collapsed failed profile reads and inactive accounts into
  // this one message. Treat that legacy denial as unverified access, not bad billing.
  const legacySubscriptionDenial = backendMessage === 'An active subscription is required to create clips. Update your billing information and try again.';
  throw new TopClipAutoApiError(
    "request",
    legacySubscriptionDenial
      ? 'We could not verify active access for your account. If you already paid, contact support; you do not need to purchase again.'
      : backendMessage === BACKEND_SOURCE_VERIFICATION_FAILURE
      ? TOPCLIP_SOURCE_VERIFICATION_MESSAGE
      : backendMessage,
    { status: response.status, ...metadata, ...(legacySubscriptionDenial ? { error: 'subscription_verification_unavailable' } : {}) }
  );
};

const authorizedFetch = async (
  url: string,
  token: string,
  init: RequestInit,
  fetchImplementation: FetchImplementation
): Promise<Response> => {
  const accessToken = requiredToken(token);
  try {
    return await fetchImplementation(url, {
      ...init,
      headers: {
        Accept: "application/json",
        ...init.headers,
        Authorization: `Bearer ${accessToken}`,
      },
    });
  } catch {
    throw new TopClipAutoApiError(
      "network",
      "TopClip is unreachable right now. Check your connection and try again."
    );
  }
};

const exactOptions = (options: TopClipAutoOptions): TopClipAutoOptions => ({
  clip_count_mode: options.clip_count_mode,
  clips_requested: options.clips_requested,
  clip_length_choice: options.clip_length_choice,
  captions_enabled: options.captions_enabled,
  caption_style_id: options.caption_style_id,
  ai_title_enabled: options.ai_title_enabled,
});

export const submitTopClipAutoJob = async (
  youtubeUrl: string,
  options: TopClipAutoOptions,
  token: string,
  fetchImplementation: FetchImplementation = fetch
): Promise<TopClipAutoSubmitResponse> => {
  const validation = validateLandingYoutubeUrl(youtubeUrl);
  if (!validation.ok) {
    throw new TopClipAutoApiError(
      "request",
      validation.message
    );
  }

  const formData = new FormData();
  formData.append("youtube_url", validation.url);
  formData.append("options", JSON.stringify(exactOptions(options)));

  const response = await authorizedFetch(
    config.endpoints.modalProcessVideo,
    token,
    { method: "POST", body: formData },
    fetchImplementation
  );
  if (!response.ok) await throwForHttpError(response);
  const payload = await readJson(response);
  if (
    !isRecord(payload) ||
    typeof payload.job_id !== "string" ||
    payload.job_id.trim() === "" ||
    payload.status !== "queued"
  ) {
    throw new TopClipAutoApiError(
      "invalid_response",
      "TopClip did not confirm that the generation was queued.",
      { status: response.status }
    );
  }

  return {
    job_id: payload.job_id,
    status: "queued",
    message:
      typeof payload.message === "string" ? payload.message : "Generation queued",
    settings: exactOptions(options),
  };
};

export const getTopClipAutoJobStatus = async (
  jobId: string,
  token: string,
  fetchImplementation: FetchImplementation = fetch
): Promise<TopClipAutoJobStatus> => {
  const encodedJobId = requiredPathSegment(jobId, "Job ID");
  const response = await authorizedFetch(
    `${config.API_BASE_URL}/api/job-status/${encodedJobId}`,
    token,
    { method: "GET" },
    fetchImplementation
  );
  if (!response.ok) await throwForHttpError(response);
  const payload = await readJson(response);
  if (
    !isRecord(payload) ||
    typeof payload.job_id !== "string" ||
    typeof payload.status !== "string"
  ) {
    throw new TopClipAutoApiError(
      "invalid_response",
      "TopClip returned an invalid generation status.",
      { status: response.status }
    );
  }

  return {
    job_id: payload.job_id,
    status: payload.status,
    progress:
      typeof payload.progress === "number" && Number.isFinite(payload.progress)
        ? payload.progress
        : 0,
    message: typeof payload.message === "string" ? payload.message : "",
    current_step:
      typeof payload.current_step === "string" ? payload.current_step : null,
    total_clips:
      typeof payload.total_clips === "number" &&
      Number.isInteger(payload.total_clips) &&
      payload.total_clips >= 0
        ? payload.total_clips
        : 0,
  };
};

const optionalString = (
  value: Record<string, unknown>,
  key: string
): string | undefined =>
  typeof value[key] === "string" ? (value[key] as string) : undefined;

const optionalNumber = (
  value: Record<string, unknown>,
  key: string
): number | undefined =>
  typeof value[key] === "number" && Number.isFinite(value[key])
    ? (value[key] as number)
    : undefined;

const parseClip = (value: unknown): TopClipAutoClip => {
  if (
    !isRecord(value) ||
    typeof value.filename !== "string" ||
    value.filename.trim() === ""
  ) {
    throw new TopClipAutoApiError(
      "invalid_response",
      "TopClip returned invalid private clip metadata."
    );
  }
  return {
    id: optionalString(value, "id"),
    filename: value.filename,
    title: optionalString(value, "title"),
    duration: optionalNumber(value, "duration"),
    file_size: optionalNumber(value, "file_size"),
    hook_title: optionalString(value, "hook_title"),
    viral_potential: optionalString(value, "viral_potential"),
    stream_url: optionalString(value, "stream_url"),
  };
};

export const getTopClipAutoClips = async (
  jobId: string,
  token: string,
  fetchImplementation: FetchImplementation = fetch
): Promise<TopClipAutoClipsResponse> => {
  const encodedJobId = requiredPathSegment(jobId, "Job ID");
  const response = await authorizedFetch(
    `${config.API_BASE_URL}/api/clips/by-job/${encodedJobId}`,
    token,
    { method: "GET" },
    fetchImplementation
  );
  if (!response.ok) await throwForHttpError(response);
  const payload = await readJson(response);
  if (!isRecord(payload) || !Array.isArray(payload.clips)) {
    throw new TopClipAutoApiError(
      "invalid_response",
      "TopClip returned an invalid private results response.",
      { status: response.status }
    );
  }
  const clips = payload.clips.map(parseClip);
  return {
    job_id:
      typeof payload.job_id === "string" ? payload.job_id : jobId,
    total:
      typeof payload.total === "number" && Number.isInteger(payload.total)
        ? payload.total
        : clips.length,
    clips,
  };
};

export const downloadTopClipAutoClip = async (
  jobId: string,
  filename: string,
  token: string,
  fetchImplementation: FetchImplementation = fetch
): Promise<Blob> => {
  const encodedJobId = requiredPathSegment(jobId, "Job ID");
  const encodedFilename = requiredPathSegment(filename, "Clip filename");
  const response = await authorizedFetch(
    `${config.API_BASE_URL}/api/clip/${encodedJobId}/${encodedFilename}`,
    token,
    { method: "GET", headers: { Accept: "video/mp4" } },
    fetchImplementation
  );
  if (!response.ok) await throwForHttpError(response);
  try {
    return await response.blob();
  } catch {
    throw new TopClipAutoApiError(
      "invalid_response",
      "TopClip returned an unreadable clip download.",
      { status: response.status }
    );
  }
};
